import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CRED_LABELS, CRED_TYPES, formatDate, PSV_STATUS, STATUS_META } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import MaskedValue from "@/components/MaskedValue";
import { toast } from "sonner";
import { ArrowLeft, Plus, PencilSimple, Trash, CheckCircle, FileArrowUp, X, FilePdf, FileCsv, DownloadSimple, EnvelopeSimple, Copy } from "@phosphor-icons/react";

export default function PractitionerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [showCred, setShowCred] = useState(null); // null=closed, {}=new, cred=edit
  const [showInvite, setShowInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/practitioners/${id}`);
      setP(r.data);
    } catch {
      toast.error("Not found");
      nav("/practitioners");
    }
  };
  useEffect(() => { load(); }, [id]);

  const delCred = async (cid) => {
    if (!window.confirm("Delete this credential?")) return;
    await api.delete(`/credentials/${cid}`);
    toast.success("Deleted");
    load();
  };

  const markVerified = async (cred) => {
    const payload = { ...cred, psv_status: "VERIFIED", psv_verified_at: new Date().toISOString() };
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.status;
    await api.put(`/credentials/${cred.id}`, payload);
    toast.success("Marked verified");
    load();
  };

  const exportFile = async (kind) => {
    try {
      const r = await api.get(`/practitioners/${id}/export/${kind}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: kind === "pdf" ? "application/pdf" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p?.last_name || "practitioner"}_${p?.first_name || ""}_credentialing.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} export downloaded`);
    } catch (err) {
      toast.error(`${kind.toUpperCase()} export failed`);
    }
  };

  if (!p) return <div className="text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="practitioner-detail">
      <Link to="/practitioners" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-600 hover:text-slate-900" data-testid="back-to-roster-link">
        <ArrowLeft size={14} /> Back to roster
      </Link>

      <header className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 border-b border-gray-200 pb-6">
        <div>
          <div className="overline text-gray-500">Practitioner file · {p.title}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">
            {p.first_name} {p.last_name}
          </h1>
          <div className="text-sm text-gray-600 mt-2">
            {p.specialty || "—"} · NPI{" "}
            <MaskedValue
              value={p.npi}
              kind="npi"
              field="npi"
              resourceType="practitioner"
              resourceId={p.id}
              className="text-sm"
            />
          </div>
          <div className="text-xs text-gray-500 mt-1 font-mono">{p.email || "—"} · {p.phone || "—"}</div>
          <div className="mt-4"><StatusBadge status={p.status_summary?.worst_status || "NO_DATE"} /></div>
        </div>
        <div className="flex items-start gap-2">
          <button onClick={() => exportFile("pdf")} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 inline-flex items-center gap-2" data-testid="export-pdf-btn">
            <FilePdf size={14} weight="bold" /> Export PDF
          </button>
          <button onClick={() => exportFile("csv")} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 inline-flex items-center gap-2" data-testid="export-csv-btn">
            <FileCsv size={14} weight="bold" /> Export CSV
          </button>
          <Link to={`/compliance?practitioner=${p.id}`} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100" data-testid="view-compliance-btn">Compliance</Link>
          <button onClick={() => setShowInvite(true)} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100 inline-flex items-center gap-2" data-testid="invite-portal-btn">
            <EnvelopeSimple size={14} weight="bold" /> {p.user_id ? "Re-invite to portal" : "Invite to portal"}
          </button>
          <button onClick={() => setShowCred({})} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2" data-testid="add-credential-btn">
            <Plus size={14} weight="bold" /> Add credential
          </button>
        </div>
      </header>

      {/* credential list */}
      <section className="bg-white border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="overline text-gray-500">Credential Ledger</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5">All tracked items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="practitioner-credentials-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gray-500 border-b border-gray-200">
                <th className="px-6 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Number</th>
                <th className="px-4 py-3 font-semibold">Issuer</th>
                <th className="px-4 py-3 font-semibold">Issued</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">PSV</th>
                <th className="px-4 py-3 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {p.credentials.length === 0 && (
                <tr><td colSpan="8" className="p-10 text-center text-gray-500 text-sm">No credentials tracked yet.</td></tr>
              )}
              {p.credentials.map((c) => (
                <tr key={c.id} className="data-row border-b border-gray-100">
                  <td className="px-6 py-3 font-medium text-slate-900">{CRED_LABELS[c.credential_type] || c.credential_type}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <MaskedValue
                      value={c.credential_number}
                      kind={c.credential_type === "DEA" ? "dea" : "license"}
                      field={`credential_number_${c.credential_type}`}
                      resourceType="credential"
                      resourceId={c.id}
                    />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{c.issuer || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{formatDate(c.issue_date)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{formatDate(c.expiration_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={credStatus(c.expiration_date)} /></td>
                  <td className="px-4 py-3 text-xs font-mono">
                    <span className={`inline-block px-2 py-0.5 ${c.psv_status === "VERIFIED" ? "bg-emerald-100 text-emerald-800" : c.psv_status === "PENDING" ? "bg-sky-100 text-sky-800" : "bg-gray-100 text-gray-700"}`}>
                      {c.psv_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      {c.psv_status !== "VERIFIED" && (
                        <button onClick={() => markVerified(c)} title="Mark PSV verified" className="text-emerald-700 hover:text-emerald-900" data-testid={`verify-${c.id}`}>
                          <CheckCircle size={16} weight="bold" />
                        </button>
                      )}
                      <button onClick={() => setShowCred(c)} title="Edit" className="text-gray-600 hover:text-slate-900" data-testid={`edit-cred-${c.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => delCred(c.id)} title="Delete" className="text-red-600 hover:text-red-800" data-testid={`delete-cred-${c.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* committee decisions */}
      <section className="bg-white border border-gray-200 p-6">
        <div className="overline text-gray-500">Committee log</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Decisions</h2>
        {p.committee_decisions.length === 0 ? (
          <div className="text-sm text-gray-500">No committee decisions yet. Record an initial or re-credentialing decision from the Committee page.</div>
        ) : (
          <ul className="space-y-3">
            {p.committee_decisions.map((d) => (
              <li key={d.id} className="border-l-2 border-slate-900 pl-4 py-1">
                <div className="text-sm font-semibold text-slate-900">{d.decision} — <span className="font-mono text-xs">{d.decision_type}</span></div>
                <div className="text-xs text-gray-600 mt-0.5">{formatDate(d.decision_date)} · next review {formatDate(d.next_review_date)}</div>
                <div className="text-xs text-gray-500 mt-1">{d.notes}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* documents */}
      <section className="bg-white border border-gray-200 p-6">
        <div className="overline text-gray-500">Document vault</div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Scanned files</h2>
        {p.documents.length === 0 ? (
          <div className="text-sm text-gray-500 flex items-center justify-between">
            <span>No documents uploaded.</span>
            <Link to="/upload" className="text-xs font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
              <FileArrowUp size={14} /> Upload scans
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {p.documents.map((d) => (
              <li key={d.id} className="py-2 text-sm flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">{d.original_filename}</div>
                  <div className="text-xs text-gray-500 font-mono">{d.content_type} · {(d.size/1024).toFixed(1)} KB · {formatDate(d.created_at)}</div>
                </div>
                <div className="text-xs text-gray-500">
                  {d.ocr_data?.document_type && <span className="font-mono">{d.ocr_data.document_type}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showCred !== null && (
        <CredentialModal
          initial={showCred}
          practitionerId={p.id}
          onClose={() => setShowCred(null)}
          onSaved={() => { setShowCred(null); load(); }}
        />
      )}

      {showInvite && (
        <InvitePortalModal
          practitioner={p}
          onClose={() => { setShowInvite(false); setInviteResult(null); }}
          onSuccess={(res) => { setInviteResult(res); load(); }}
          inviteResult={inviteResult}
        />
      )}
    </div>
  );
}

function InvitePortalModal({ practitioner, onClose, onSuccess, inviteResult }) {
  const [email, setEmail] = useState(practitioner.email || "");
  const [phone, setPhone] = useState(practitioner.phone || "");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await api.post(`/practitioners/${practitioner.id}/invite`, {
        email,
        phone,
        app_url: window.location.origin,
      });
      const ok = r.data.sms_status?.status === "sent";
      if (ok) {
        toast.success(`Invitation texted to ${phone}`);
      } else if (r.data.already_existed) {
        toast.success(`Re-linked existing portal account for ${email}`);
      } else {
        toast.warning("Account created — SMS delivery failed (see fallback below)");
      }
      onSuccess(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setSending(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="invite-portal-modal">
      <div className="bg-white border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <div className="overline text-gray-500">Practitioner self-service portal</div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">
              {inviteResult ? "Invite sent" : "Invite to portal"}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-slate-900" data-testid="invite-modal-close"><X size={18} /></button>
        </div>
        {!inviteResult ? (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="text-sm text-gray-700">
              Creates a portal account for <strong>{practitioner.first_name} {practitioner.last_name}</strong> and
              <strong> texts the login link &amp; temporary password to their cell phone</strong> via Twilio.
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Cell phone *</label>
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15042288356"
                className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm font-mono focus:border-slate-900 focus:outline-none"
                data-testid="invite-portal-phone"
                autoFocus={!phone}
              />
              <div className="text-[11px] text-gray-500 mt-1">E.164 format (e.g. +15045551234). Saved to the practitioner record.</div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Email *</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                data-testid="invite-portal-email"
                autoFocus={!!phone}
              />
              <div className="text-[11px] text-gray-500 mt-1">Used as their username for signing in.</div>
            </div>
            <div className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-900">
              <strong>Note:</strong> If SMS delivery fails (no phone, Twilio limits), the login link &amp; temp password
              are shown here so you can share them manually.
            </div>
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
              <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Cancel</button>
              <button type="submit" disabled={sending} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-60 inline-flex items-center gap-2" data-testid="invite-portal-submit">
                <EnvelopeSimple size={14} weight="bold" /> {sending ? "Sending…" : "Text invitation"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-4" data-testid="invite-portal-result">
            <div className={`px-3 py-2 text-xs ${inviteResult.sms_status?.status === "sent" ? "bg-emerald-50 text-emerald-900 border-l-2 border-emerald-500" : "bg-amber-50 text-amber-900 border-l-2 border-amber-400"}`}>
              {inviteResult.sms_status?.status === "sent"
                ? <>✓ Invitation texted to <strong>{inviteResult.phone}</strong>.</>
                : inviteResult.already_existed
                  ? <>This email already has a portal account — re-linked to this practitioner. No new password was set.</>
                  : <>SMS delivery failed: <span className="font-mono">{inviteResult.sms_status?.error || inviteResult.sms_status?.reason}</span>. Share the credentials below manually.</>}
            </div>
            {inviteResult.temp_password && (
              <>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Login URL</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono truncate" data-testid="invite-login-url">{inviteResult.login_url}</code>
                    <button onClick={() => copy(inviteResult.login_url)} className="border border-gray-300 p-2 hover:bg-gray-100" title="Copy"><Copy size={14} /></button>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Email</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono">{inviteResult.email}</code>
                    <button onClick={() => copy(inviteResult.email)} className="border border-gray-300 p-2 hover:bg-gray-100" title="Copy"><Copy size={14} /></button>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Temporary password</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border border-gray-200 px-3 py-2 text-xs font-mono" data-testid="invite-temp-password">{inviteResult.temp_password}</code>
                    <button onClick={() => copy(inviteResult.temp_password)} className="border border-gray-300 p-2 hover:bg-gray-100" title="Copy"><Copy size={14} /></button>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">Visible once. The practitioner must change it after first login. We don't store retrievable passwords.</div>
                </div>
              </>
            )}
            <div className="flex justify-end pt-2 border-t border-gray-200">
              <button onClick={onClose} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700" data-testid="invite-portal-done">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function credStatus(exp) {
  if (!exp) return "NO_DATE";
  const d = Math.round((new Date(exp) - new Date()) / (1000 * 60 * 60 * 24));
  if (d < 0) return "EXPIRED";
  if (d <= 7) return "EXPIRING_7";
  if (d <= 30) return "EXPIRING_30";
  if (d <= 60) return "EXPIRING_60";
  if (d <= 90) return "EXPIRING_90";
  return "CURRENT";
}

function CredentialModal({ initial, practitionerId, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    credential_type: initial.credential_type || "STATE_LICENSE",
    credential_number: initial.credential_number || "",
    issuer: initial.issuer || "",
    issue_date: (initial.issue_date || "").slice(0,10),
    expiration_date: (initial.expiration_date || "").slice(0,10),
    psv_status: initial.psv_status || "PENDING",
    psv_source: initial.psv_source || "",
    notes: initial.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, practitioner_id: practitionerId };
      if (isNew) {
        await api.post("/credentials", payload);
      } else {
        await api.put(`/credentials/${initial.id}`, payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="credential-modal">
      <div className="bg-white border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <div className="overline text-gray-500">{isNew ? "Add" : "Edit"} credential</div>
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mt-0.5">{CRED_LABELS[form.credential_type]}</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-slate-900" data-testid="cred-modal-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Credential type *</label>
            <select value={form.credential_type} onChange={set("credential_type")} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="cred-type">
              {CRED_TYPES.map((t) => <option key={t} value={t}>{CRED_LABELS[t]}</option>)}
            </select>
          </div>
          <F label="Credential #" value={form.credential_number} onChange={set("credential_number")} testid="cred-number" />
          <F label="Issuer" value={form.issuer} onChange={set("issuer")} testid="cred-issuer" />
          <F label="Issue date" type="date" value={form.issue_date} onChange={set("issue_date")} testid="cred-issue-date" />
          <F label="Expiration date" type="date" value={form.expiration_date} onChange={set("expiration_date")} testid="cred-exp-date" />
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">PSV status</label>
            <select value={form.psv_status} onChange={set("psv_status")} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="cred-psv-status">
              {PSV_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <F label="PSV source" value={form.psv_source} onChange={set("psv_source")} testid="cred-psv-source" />
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="cred-notes" />
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={saving} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 disabled:opacity-60" data-testid="cred-save-btn">{saving ? "Saving…" : (isNew ? "Add credential" : "Save changes")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type="text", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">{label}</label>
      <input type={type} value={value} onChange={onChange} className="mt-1 w-full border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none" data-testid={testid} />
    </div>
  );
}
